/**
 * The Today briefing (13-09): the words above the queue, as pure functions.
 *
 *   - `greeting(now, firstName)`: "Good morning, Sam", in Melbourne time whatever the host clock says;
 *   - `countLine(queue, now)`: "4 things for you, 1 overdue" / "1 thing for you" / "" when empty;
 *   - `patchLine(lens, metrics)`: one calm sentence about the person's patch (13-05
 *     `myPatchRegister`), refs and counts only, never a person or a client;
 *   - `recordsLine(groups)` (13-29): "3 records waiting on you, 1 decision due", counts only;
 *   - `dueLabel(row, now)` and `compareQueue(now)`: the card's due text and the queue order.
 *
 * Overdue means `DueDate` is before Melbourne today. The due date itself is not overdue.
 * Every Melbourne date comes from `Intl` with an explicit time zone, never the host clock.
 *
 * No React, no I/O.
 */

import { melbourneDay } from './actionVerbs';
import type { ActedAt } from './actionVerbs';
import type { ActionLens, PatchRegister } from './actionAuthority';
import { waitingCounts, type WaitingGroup } from './recordQueue';
import type { ActionRow } from '../types/actionRegister';

// ---------------------------------------------------------------------------
// Melbourne time
// ---------------------------------------------------------------------------

const MELBOURNE_HOUR = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  hour: 'numeric',
  hourCycle: 'h23',
});

/** The Melbourne hour (0–23) of an instant, DST-safe. */
export function melbourneHour(now: ActedAt): number {
  const d = now instanceof Date ? now : new Date(now);
  const part = MELBOURNE_HOUR.formatToParts(d).find((p) => p.type === 'hour')?.value ?? '0';
  return Number(part) % 24;
}

/** "Saturday 4 October", in Melbourne whatever the host clock says. */
export function melbourneLongDate(now: ActedAt): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Melbourne',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
    .format(now instanceof Date ? now : new Date(now))
    .replace(',', '');
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The calendar day a `DueDate` names, `YYYY-MM-DD`. A bare date is taken as written. A date-only
 * column read through Graph arrives as an instant near midnight in the site's time zone, so it is
 * read as its Melbourne calendar day.
 */
export function dueDay(iso: string | null | undefined): string | null {
  const s = (iso ?? '').trim();
  if (!s) return null;
  if (ISO_DAY.test(s)) return s;
  return Number.isNaN(Date.parse(s)) ? null : melbourneDay(s);
}

/** Whole calendar days from day `a` to day `b` (both `YYYY-MM-DD`). Positive when `b` is later. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

// ---------------------------------------------------------------------------
// Greeting
// ---------------------------------------------------------------------------

export type Salutation = 'Good morning' | 'Good afternoon' | 'Good evening';

/** Morning before 12:00, afternoon until 17:00, evening after, Melbourne time. */
export function salutation(now: ActedAt): Salutation {
  const h = melbourneHour(now);
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** "Andy Watt" → "Andy"; "Watt, Andy" → "Andy"; an email → its first word. */
export function firstNameOf(displayName: string | null | undefined): string {
  const s = (displayName ?? '').trim();
  if (!s) return '';
  if (s.includes(',')) return s.split(',')[1]?.trim().split(/\s+/)[0] ?? '';
  return s.split('@')[0].split(/[\s.]+/)[0] ?? '';
}

/** "Good morning, Sam" (or just "Good morning" when there is no name). */
export function greeting(now: ActedAt, firstName: string | null | undefined): string {
  const name = (firstName ?? '').trim();
  return name ? `${salutation(now)}, ${name}` : salutation(now);
}

// ---------------------------------------------------------------------------
// Due dates and the count line
// ---------------------------------------------------------------------------

/** `DueDate` before Melbourne today. Due today is not overdue. */
export function isOverdue(row: Pick<ActionRow, 'dueDate'>, now: ActedAt): boolean {
  const due = dueDay(row.dueDate);
  return due !== null && due < melbourneDay(now);
}

const DAY_LABEL = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** "Wed 8 Oct" for a `YYYY-MM-DD` day. */
export function shortDay(day: string): string {
  return DAY_LABEL.format(new Date(`${day}T12:00:00Z`)).replace(',', '');
}

export interface DueText {
  text: string;
  overdue: boolean;
}

/** "Due today" / "Due tomorrow" / "Due Thu 8 Oct" / "1 day overdue" / "3 days overdue"; null when no date. */
export function dueLabel(row: Pick<ActionRow, 'dueDate'>, now: ActedAt): DueText | null {
  const due = dueDay(row.dueDate);
  if (due === null) return null;
  const diff = daysBetween(melbourneDay(now), due);
  if (diff < 0) {
    const late = -diff;
    return { text: `${late} ${late === 1 ? 'day' : 'days'} overdue`, overdue: true };
  }
  if (diff === 0) return { text: 'Due today', overdue: false };
  if (diff === 1) return { text: 'Due tomorrow', overdue: false };
  return { text: `Due ${shortDay(due)}`, overdue: false };
}

export interface CountParts {
  count: number;
  overdue: number;
}

export function countParts(queue: readonly Pick<ActionRow, 'dueDate'>[], now: ActedAt): CountParts {
  return { count: queue.length, overdue: queue.filter((r) => isOverdue(r, now)).length };
}

/** "4 things for you, 1 overdue" · "1 thing for you" · "" when the queue is empty. */
export function countLine(queue: readonly Pick<ActionRow, 'dueDate'>[], now: ActedAt): string {
  const { count, overdue } = countParts(queue, now);
  if (count === 0) return '';
  const head = `${count} ${count === 1 ? 'thing' : 'things'} for you`;
  return overdue > 0 ? `${head}, ${overdue} overdue` : head;
}

// ---------------------------------------------------------------------------
// The patch line
// ---------------------------------------------------------------------------

/**
 * What the patch line counts. Oversight's patch is the Action Register itself. `overdue` is 13-16's per-register due
 * column (Incidents `ResolveBy`, Complaints `TargetResolutionOn`, `useRegisterMetrics` via `registerReaders`).
 * `reportable` (13-29) is the open incidents whose `ReportabilityDecision` is reportable (never the frozen reporting
 * column); it is read for Incidents only.
 */
export type PatchMetrics =
  | { register: 'actions'; waiting: number; toVerify?: number | null }
  | { register: 'incidents' | 'complaints'; open: number; overdue: number; reportable?: number };

const REGISTER_LABEL: Record<Exclude<PatchRegister, 'actions'>, string> = {
  incidents: 'Incidents',
  complaints: 'Complaints',
};

/**
 * One calm sentence from the lens's `myPatchRegister` (13-05):
 *   - Oversight: "Release queue: 190 waiting · 225 to verify";
 *   - CareManager / HRWHS: "Incidents: 2 open, none past due";
 *   - Finance: "Complaints: 1 open, 1 past due".
 * Counts only: it never names a person or a client. "" when the lens has no patch or the metrics
 * are for a different register.
 */
export function patchLine(
  lens: Pick<ActionLens, 'myPatchRegister'>,
  metrics: PatchMetrics | null | undefined,
): string {
  const register = lens.myPatchRegister;
  if (!register || !metrics || metrics.register !== register) return '';
  if (metrics.register === 'actions') {
    const waiting = metrics.waiting > 0 ? `${metrics.waiting} waiting` : 'none waiting';
    const verify = typeof metrics.toVerify === 'number' ? ` · ${metrics.toVerify} to verify` : '';
    return `Release queue: ${waiting}${verify}`;
  }
  const label = REGISTER_LABEL[metrics.register];
  if (metrics.open <= 0) return `${label}: none open`;
  const late = metrics.overdue > 0 ? `${metrics.overdue} past due` : 'none past due';
  const reportable =
    metrics.register === 'incidents' && typeof metrics.reportable === 'number' && metrics.reportable > 0
      ? `, ${metrics.reportable} reportable`
      : '';
  return `${label}: ${metrics.open} open, ${late}${reportable}`;
}

// ---------------------------------------------------------------------------
// The records line (13-29)
// ---------------------------------------------------------------------------

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The briefing's line about the register records waiting on the person (13-29), after the count line:
 * "3 records waiting on you, 1 decision due" (then notices due and past due when there are any). Counts only, as
 * `patchLine`: never a person, a client, a title or a ref. "" when nothing is waiting.
 */
export function recordsLine(groups: readonly WaitingGroup[]): string {
  const n = waitingCounts(groups);
  if (n.records === 0) return '';
  const parts = [`${plural(n.records, 'record', 'records')} waiting on you`];
  if (n.decisions > 0) parts.push(`${plural(n.decisions, 'decision', 'decisions')} due`);
  if (n.notices > 0) parts.push(`${plural(n.notices, 'notice', 'notices')} due`);
  if (n.pastDue > 0) parts.push(`${n.pastDue} past due`);
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// Queue order
// ---------------------------------------------------------------------------

function instant(iso: string | null | undefined): number {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Overdue first, then the earliest due date (none last), then the earliest release, then id. */
export function compareQueue(now: ActedAt): (a: ActionRow, b: ActionRow) => number {
  return (a, b) => {
    const oa = isOverdue(a, now) ? 0 : 1;
    const ob = isOverdue(b, now) ? 0 : 1;
    if (oa !== ob) return oa - ob;
    const da = dueDay(a.dueDate) ?? '9999-12-31';
    const db = dueDay(b.dueDate) ?? '9999-12-31';
    if (da !== db) return da < db ? -1 : 1;
    const ra = instant(a.releasedAt);
    const rb = instant(b.releasedAt);
    if (ra !== rb) return ra < rb ? -1 : 1;
    return a.id - b.id;
  };
}
